import { UseQueryResult, useMutation, useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";
import { minutes } from "@/utils";

interface TaxFilingStatus {
  id: number;
  name: string;
}

interface AccountingMethod {
  id: number;
  name: string;
}

interface Jurisdiction {
  id: number;
  name: string;
  taxFilingStatuses: TaxFilingStatus[];
  acccountingMethods: AccountingMethod[];
}

export const useTaxSettingQueryKey = ["useTaxSetting"];

export const useTaxSetting = ({
  refetchOnWindowFocus = true,
  staleTime = 1 * minutes,
  onUpdateTaxSettingSuccess,
  onUpdateTaxSettingError,
}: {
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
  onUpdateTaxSettingSuccess?: () => void;
  onUpdateTaxSettingError?: () => void;
}) => {
  const { user, getToken } = useAuth();
  const email = user?.email;

  const getTaxSetting = async (): Promise<Jurisdiction> => {
    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(`/tax/setting`);
      return res.data;
    } catch (error) {
      console.error(
        `[useTaxSetting] Failed to fetch tax setting config`,
        error
      );
      throw error;
    }
  };

  const mutationUpdateTaxSetting = useMutation({
    mutationFn: async (payload: {
      jurisdictionId: number;
      accountingMethodId: number;
      costTrackingMethodId: number | null | undefined;
      filingStatusId: number;
      cgtRate?: number;
      cgtBand?: string;
      cgtAllowance?: number;
      incomeRate?: number;
    }) => {
      const token = await getToken();
      await authorizedAxios(token).post(`/tax/setting`, payload);
    },
    onSuccess: () => {
      onUpdateTaxSettingSuccess?.(); // Refresh the config after update
    },
    onError: (err) => {
      onUpdateTaxSettingError?.();
      console.error("[useTaxSetting] Failed to update tax setting", err);
    },
  });

  const {
    isLoading: isQueryLoading,
    isIdle,
    error,
    data,
    refetch,
  }: UseQueryResult<Jurisdiction, unknown> = useQuery({
    queryKey: useTaxSettingQueryKey,
    queryFn: getTaxSetting,
    enabled: !!email,
    refetchOnWindowFocus,
    staleTime,
    keepPreviousData: true,
  });

  return {
    isQueryLoading,
    isIdle,
    error,
    taxConfig: data,
    refetch,
    updateTaxSetting: mutationUpdateTaxSetting.mutate,
    isUpdating: mutationUpdateTaxSetting.isPending, // loading state
  };
};
